import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  getGetUserPodcastsQueryKey,
  useDeletePodcast,
  useUpdatePodcast,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreatePodcastFromReferenceRequestDtoGenerationType,
  ListPodcastResponseDto,
  PodcastResponseDto,
  PodcastTranscriptItem,
} from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import { useToast } from "@packages/contexts/ToastContext";
import { TrashIcon, Users, Wand2 } from "lucide-react";
import DeleteConfirmPopUp from "../../tools/components/tool_components/DeleteConfirmPopUp";
import { Button, Text } from "@core/index";
import PdfDownloadButton from "@features/tools/components/pdf/PdfDownloadButton";
import PodcastPdf from "./PodcastPdf";
import ImageOrVideo from "@features/tools/components/tool_components/ImageOrVideo";
import Select, { StylesConfig } from "react-select";
import { useAppSelector } from "@packages/store/hooks";

interface Question {
  questionText: string;
  questionAnswer: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface CustomAudioProps {
  id?: number;
  url?: string;
  name?: string;
  transcript?: PodcastTranscriptItem[] | undefined;
  createdAt?: string;
  imageUrl?: string;
  videoUrl?: string;
  isImageLoading?: boolean;
  podcastGenerationType?: CreatePodcastFromReferenceRequestDtoGenerationType;
  questions?: Question[];
  onRegenClick?: (
    podcastId: number | undefined,
    podcastGenerationType: CreatePodcastFromReferenceRequestDtoGenerationType,
  ) => void;
  onAssignClick?: (podcastId: number | undefined, podcastTitle: string) => void;
  author?: string;
  openPdfCustomizationModal?: (podcastId: number | undefined) => void;
  // Global podcast control props
  currentlyPlayingId?: number | null;
  onPlayStateChange?: (id: number | null) => void;
}

// Global variable to track currently playing podcast
let globalCurrentlyPlaying: number | null = null;
let globalPlayStateListeners: Set<(id: number | null) => void> = new Set();

const PodcastAudioCard = ({
  id,
  url,
  name,
  transcript,
  createdAt,
  imageUrl,
  videoUrl,
  isImageLoading,
  author,
  podcastGenerationType,
  questions,
  onRegenClick,
  onAssignClick,
  openPdfCustomizationModal,
}: CustomAudioProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [editedName, setEditedName] = useState<string | undefined>(name);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [playbackSpeed, setPlaybackSpeed] = useState<string>("1");
  const [showTranscript, setShowTranscript] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>();
  const [isSeeking, setIsSeeking] = useState<boolean>(false);

  const getPodcastsQueryKey = getGetUserPodcastsQueryKey();
  const queryClient = useQueryClient();

  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );

  // Memoize the PDF customization modal handler to prevent re-renders
  const memoizedOpenPdfCustomizationModal = useMemo(() => {
    return openPdfCustomizationModal
      ? () => openPdfCustomizationModal(id)
      : undefined;
  }, [openPdfCustomizationModal, id]);

  // Global play state management
  useEffect(() => {
    const handleGlobalPlayStateChange = (playingId: number | null) => {
      if (playingId !== id && isPlaying) {
        // Stop this podcast if another one is playing
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
    };

    globalPlayStateListeners.add(handleGlobalPlayStateChange);

    return () => {
      globalPlayStateListeners.delete(handleGlobalPlayStateChange);
    };
  }, [id, isPlaying]);

  // Notify all listeners when global play state changes
  const notifyGlobalPlayStateChange = useCallback(
    (playingId: number | null) => {
      globalCurrentlyPlaying = playingId;
      globalPlayStateListeners.forEach((listener) => listener(playingId));
    },
    [],
  );

  const deletePodcast = useDeletePodcast({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(getPodcastsQueryKey);
        queryClient.setQueryData(
          getPodcastsQueryKey,
          (oldData: ListPodcastResponseDto) => ({
            ...oldData,
            data: oldData?.data?.filter(
              (podcast: PodcastResponseDto) => podcast.id !== variables.id,
            ),
          }),
        );
        return { previousData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(getPodcastsQueryKey, context?.previousData);
        console.error("Error deleting podcast:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getPodcastsQueryKey });
      },
    },
  });

  const updatePodcast = useUpdatePodcast({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(getPodcastsQueryKey);

        queryClient.setQueryData(
          getPodcastsQueryKey,
          (oldData: ListPodcastResponseDto) => ({
            ...oldData,
            data: oldData?.data?.map((podcast: PodcastResponseDto) =>
              podcast.id === variables.id
                ? {
                    ...podcast,
                    title: variables.data.title,
                  }
                : podcast,
            ),
          }),
        );

        return { previousData };
      },

      onError: (error, _variables, context) => {
        queryClient.setQueryData(getPodcastsQueryKey, context?.previousData);
        console.error("Error updating podcast:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getPodcastsQueryKey });
      },
    },
  });

  useEffect(() => {
    if (audioRef.current) {
      const handleLoadedMetadata = () => {
        setDuration(audioRef.current?.duration || 0);
      };

      audioRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener(
            "loadedmetadata",
            handleLoadedMetadata,
          );
        }
      };
    }
  }, [url]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handlePlayPause = useCallback(() => {
    if (audioRef.current && id) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        notifyGlobalPlayStateChange(null);
      } else {
        // Stop any currently playing podcast
        if (globalCurrentlyPlaying && globalCurrentlyPlaying !== id) {
          notifyGlobalPlayStateChange(null);
        }

        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            notifyGlobalPlayStateChange(id);
          })
          .catch((error) => {
            console.error("Error playing audio:", error);
          });
      }
    }
  }, [isPlaying, id, notifyGlobalPlayStateChange]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && !isSeeking) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, [isSeeking]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);

    if (audioRef.current) {
      // For Safari compatibility, set seeking state
      setIsSeeking(true);
      audioRef.current.currentTime = time;

      // Use requestAnimationFrame for better Safari performance
      requestAnimationFrame(() => {
        setTimeout(() => {
          setIsSeeking(false);
        }, 100);
      });
    }
  }, []);

  // Handle seek start (mouse down)
  const handleSeekStart = useCallback(() => {
    setIsSeeking(true);
  }, []);

  // Handle seek end (mouse up)
  const handleSeekEnd = useCallback(() => {
    setTimeout(() => {
      setIsSeeking(false);
    }, 50);
  }, []);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value);
      if (audioRef.current) {
        audioRef.current.volume = newVolume;
        setVolume(newVolume);
      }
    },
    [],
  );

  const handleSpeedChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSpeed = e.target.value;
      if (audioRef.current) {
        audioRef.current.playbackRate = parseFloat(newSpeed);
        setPlaybackSpeed(newSpeed);
      }
    },
    [],
  );

  const handleDownload = useCallback(() => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}.mp3`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  }, [url, name]);

  const handleUpdateTitle = useCallback(
    async (podcastId: number, newTitle: string) => {
      try {
        await updatePodcast.mutateAsync({
          id: podcastId,
          data: { title: newTitle },
        });
        addToast({
          title: t("podcast.titleUpdated"),
          variant: "success",
          duration: 3000,
        });
      } catch (error) {
        console.error("Failed to update podcast title:", error);
        addToast({
          title: t("podcast.updateError"),
          description: t("podcast.titleUpdateFailed"),
          variant: "error",
        });
      }
    },
    [updatePodcast, addToast, t],
  );

  const handleTitleSubmit = useCallback(
    async (podcastId: number | undefined, newTitle: string | undefined) => {
      if (!newTitle || !podcastId) return;
      if (newTitle === name) {
        setIsEditing(false);
        return;
      }
      handleUpdateTitle(podcastId, newTitle);
      setIsEditing(false);
    },
    [name, handleUpdateTitle],
  );

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    notifyGlobalPlayStateChange(null);
  }, [notifyGlobalPlayStateChange]);

  // Playback speed options
  const speedOptions: { value: string; label: string }[] = [
    { value: "0.5", label: "0.5x" },
    { value: "0.75", label: "0.75x" },
    { value: "1", label: "1x" },
    { value: "1.25", label: "1.25x" },
    { value: "1.5", label: "1.5x" },
    { value: "2", label: "2x" },
  ];

  // react-select styles with proper typing
  const selectStyles: StylesConfig<SelectOption, false> = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (base) => ({
        ...base,
        backgroundColor: "var(--background, #f7fafc)",
        color: "var(--foreground, #4a5568)",
        borderColor: "var(--border, #e2e8f0)",
        borderRadius: "0.5rem",
        padding: "0.125rem 0.5rem",
        minHeight: "1.75rem",
        fontSize: "0.75rem",
        lineHeight: "1rem",
        boxShadow: "none",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "var(--border-hover, #cbd5e0)",
        },
        "@media (min-width: 640px)": {
          padding: "0.25rem 0.5rem",
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
        },
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "var(--primary-500, #3182ce)"
          : state.isFocused
            ? "var(--primary-100, #bee3f8)"
            : "var(--background, #fff)",
        color: state.isSelected ? "#fff" : "var(--foreground, #4a5568)",
        fontSize: "0.75rem",
        padding: "0.5rem",
        "&:hover": {
          backgroundColor: state.isSelected
            ? "var(--primary-500, #3182ce)"
            : "var(--primary-100, #bee3f8)",
        },
        "@media (min-width: 640px)": {
          fontSize: "0.875rem",
        },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: "var(--background, #fff)",
        borderRadius: "0.5rem",
        border: "1px solid var(--border, #e2e8f0)",
        fontSize: "0.75rem",
        "@media (min-width: 640px)": {
          fontSize: "0.875rem",
        },
      }),
      singleValue: (base) => ({
        ...base,
        color: "var(--foreground, #4a5568)",
        fontSize: "0.75rem",
        "@media (min-width: 640px)": {
          fontSize: "0.875rem",
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: "var(--gray-400, #a0aec0)",
        fontSize: "0.75rem",
        "@media (min-width: 640px)": {
          fontSize: "0.875rem",
        },
      }),
    }),
    [],
  );

  return (
    <div className="relative bg-white dark:bg-dark-600 rounded-2xl shadow-xl p-0 sm:p-0 w-full max-w-xl mx-auto overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-300 group hover:shadow-2xl">
      {/* Görsel/Video */}
      <div className="aspect-w-16 aspect-h-9 h-48 sm:h-56 w-full relative overflow-hidden rounded-t-2xl bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <ImageOrVideo
          imageUrl={imageUrl}
          videoUrl={videoUrl}
          isLoading={isImageLoading}
          topic={name}
          resolution={"original"}
          createdAt={createdAt}
        />
        {/* Oynatıcı butonu görselin ortasında */}
        <button
          onClick={handlePlayPause}
          disabled={!url}
          className={`absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-900/80 rounded-full shadow-lg p-3 border-2 border-primary-500 hover:scale-105 transition-all duration-200 ${isPlaying ? "ring-2 ring-primary-500" : ""}`}
        >
          {isPlaying ? (
            <svg
              className="w-8 h-8 text-primary-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg
              className="w-8 h-8 text-primary-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      </div>
      {/* Üst bilgi ve aksiyonlar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-5 py-2 gap-2 border-b dark:border-dark-400">
        <Text
          variant="span"
          status="secondary"
          size="sm"
          className="whitespace-nowrap text-gray-500 dark:text-gray-400"
        >
          {createdAt
            ? new Date(createdAt).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "No date available"}
        </Text>
        <div className="flex flex-row gap-2 ml-auto">
          {onAssignClick && (
            <Button
              onClick={() => onAssignClick(id, name || "")}
              variant="solid"
              size="sm"
              rounded="full"
              textColor="text-primary-700 dark:text-white"
              bgColor="bg-primary-100 hover:bg-primary-200 dark:bg-primary-500 dark:hover:bg-primary-400"
              leftIcon={<Users size={16} />}
              className="text-xs sm:text-sm py-1 px-3 shadow-sm hover:scale-105 transition-transform"
            >
              {t("podcast.assignStudents")}
            </Button>
          )}
          <Button
            onClick={() => setShowDeletePopup(id)}
            variant="ghost"
            rounded="full"
            loading={deletePodcast.isPending}
            size="icon"
            textColor="text-red-500 hover:text-red-600"
            className="flex-shrink-0 hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      </div>
      {/* Başlık ve düzenleme */}
      <div className="flex items-center gap-2 min-w-0 px-5 pt-3 pb-2">
        {isEditing ? (
          <div className="flex items-center gap-2 w-full">
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="text-lg sm:text-xl font-semibold dark:text-white bg-transparent border-b-2 border-primary-500 focus:outline-none w-full px-2 py-1 rounded-md"
              autoFocus
            />
            <Button
              onClick={() => handleTitleSubmit(id, editedName)}
              variant="ghost"
              size="icon"
              rounded="full"
              textColor="text-primary-500 hover:text-primary-600"
              className="flex-shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </Button>
          </div>
        ) : (
          <div className="flex items-center w-full">
            <div className="w-0 flex-1 overflow-hidden">
              <h2
                className="text-xl sm:text-2xl font-bold truncate dark:text-white"
                title={name || ""}
              >
                {name}
              </h2>
            </div>
            <div className="flex-shrink-0 ml-2">
              <Button
                onClick={() => setIsEditing(true)}
                variant="ghost"
                size="icon"
                rounded="full"
                textColor="text-gray-400 hover:text-primary-500"
                className="flex-shrink-0"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Audio (hidden) */}
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleAudioEnded}
        className="hidden"
        preload="metadata"
      />
      {/* Oynatıcı kontrolleri */}
      <div className="px-5 pt-2 pb-4">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[40px]">
            {formatTime(currentTime)}
          </span>
          <input
            type="range"
            min={0}
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            onMouseDown={handleSeekStart}
            onMouseUp={handleSeekEnd}
            onTouchStart={handleSeekStart}
            onTouchEnd={handleSeekEnd}
            className="flex-grow h-2 rounded-full appearance-none bg-gray-200 dark:bg-gray-600 cursor-pointer accent-primary-500"
            style={{
              background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${(currentTime / duration) * 100}%, #e2e8f0 ${(currentTime / duration) * 100}%, #e2e8f0 100%)`,
            }}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[40px] text-right">
            {formatTime(duration)}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
          {/* Ses ve hız */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <svg
                className="w-5 h-5 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
              </svg>
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={volume}
                onChange={handleVolumeChange}
                className="w-16 h-2 rounded-full appearance-none bg-gray-200 dark:bg-gray-600 cursor-pointer accent-primary-500"
              />
            </div>
            <div className="w-20 flex items-center">
              <select
                value={playbackSpeed}
                onChange={handleSpeedChange}
                className="block w-full text-xs sm:text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 py-1 px-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ minWidth: 56 }}
                aria-label="Playback speed"
              >
                {speedOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Aksiyonlar */}
          <div className="flex flex-row gap-2">
            <Button
              onClick={handleDownload}
              disabled={!url}
              rounded="full"
              size="icon"
              textColor="text-primary-500 hover:text-primary-600"
              bgColor="bg-primary-500/10 hover:bg-primary-500/20"
              className={`border border-primary-500 p-2 flex-shrink-0 ${!url ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </Button>
            {onRegenClick && (
              <Button
                onClick={() => onRegenClick(id, podcastGenerationType!)}
                rounded="full"
                size="icon"
                textColor="text-blue-500 hover:text-blue-600"
                bgColor="bg-blue-500/10 hover:bg-blue-500/20"
                className="border border-blue-500 p-2 relative group overflow-hidden transition-all duration-300 flex-shrink-0 flex items-center"
              >
                <Wand2 className="w-5 h-5 transform group-hover:rotate-12 transition-transform duration-300" />
              </Button>
            )}
            <PdfDownloadButton
              pdfTitle={name || ""}
              openCustomizationModal={memoizedOpenPdfCustomizationModal}
              menuPosition="top"
              document={
                <PodcastPdf
                  customizationSettings={customizationSettings}
                  name={name || ""}
                  transcript={transcript}
                  imageUrl={imageUrl}
                  createdAt={createdAt}
                  author={author || ""}
                  questions={questions}
                />
              }
              btnTheme="bg-gradient-to-r from-purple-600 to-pink-600"
            />
          </div>
        </div>
      </div>
      {/* Transcript Section */}
      {transcript && (
        <div className="px-5 pb-4">
          <Button
            textColor="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            variant="solid"
            onClick={() => setShowTranscript(!showTranscript)}
            className="space-x-2 hover:bg-none w-full flex justify-start items-center py-1 sm:py-2 rounded-lg mt-2"
          >
            <svg
              className={`w-5 h-5 transform transition-transform ${showTranscript ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <Text variant="span" size="sm" className="sm:text-base">
              {t("podcast.transcript")}
            </Text>
          </Button>
          {showTranscript && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl max-h-60 sm:max-h-96 overflow-y-auto animate-fadeIn border border-gray-100 dark:border-gray-700">
              <div className="space-y-3">
                {transcript?.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start space-x-3 p-3 rounded-lg border transition-all duration-200 ${item.name === "shimmer" || item.name === "nova" ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500" : "bg-green-50 dark:bg-green-900/20 border-green-500"}`}
                  >
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-base font-bold ${item.name === "shimmer" || item.name === "nova" ? "bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200" : "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200"}`}
                    >
                      <Text
                        variant="span"
                        size="xs"
                        weight="medium"
                        className="sm:text-sm"
                      >
                        {item.name === "shimmer" || item.name === "nova"
                          ? "A"
                          : "B"}
                      </Text>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold mb-1 capitalize text-gray-700 dark:text-gray-200">
                        {item.displayName}
                      </div>
                      <Text
                        variant="p"
                        size="xs"
                        className="mt-1 break-words sm:text-sm text-gray-600 dark:text-gray-300"
                      >
                        {item.message}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Modals */}
      {showDeletePopup && (
        <DeleteConfirmPopUp
          title={t("podcast.confirmDelete", { title: name })}
          showDeletePopup={showDeletePopup}
          handleDelete={() => {
            if (!id) return;
            deletePodcast.mutateAsync({ id });
            setShowDeletePopup(undefined);
          }}
          setShowDeletePopup={setShowDeletePopup}
        />
      )}
    </div>
  );
};

export default PodcastAudioCard;
